/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import PropTypes from 'prop-types'
import { css, jsx } from '@emotion/core'
import {
  Button,
  CircularProgress,
  ClickAwayListener,
  Grow,
  Icon,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from '@material-ui/core'
import { theme } from '../../../constants'
import { history } from '../../App'
import AddOrganizationUsersModal from './AddOrganizationUsersModal'
import EditOrganizationUserModal from './EditOrganizationUserModal'
import { getPlanName } from '../../../utils/planName'
import DeactivateOrganizationUserModal from './DeactivateOrganizationUserModal'
import UnlinkOrganizationUserModal from './UnlinkOrganizationUserModal'

const styles = {}
styles.loadingContainer = css`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: white;
  opacity: 0.5;
  z-index: 1;
`

styles.container = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  margin: auto;
`

styles.headerContainer = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: start;
  margin-top: 10px;
  margin-bottom: 20px;
`

styles.subHeaderContainer = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`

styles.inputContainer = css`
  display: flex;
  align-items: center;
  width: calc(100% / 3);
`

styles.descriptionContainer = css`
  padding-left: 16px;
  font-size: 14px;
`

styles.headerIcon = { marginRight: '6px', color: 'white' }

styles.inputSearchContainer = css`
  display: flex;
  width: 100%;
`

styles.inputSearch = css`
  color: white;
  background: transparent;
  border: none;
  outline: 0;
  font-size: 16px;
  width: 100%;
  font-color: white;
  font-weight: 500;
  opacity: 1;
`

styles.menuHeader = {
  zIndex: '9',
  position: 'relative'
}

styles.buttonHeader = {
  height: '30px',
  padding: '6px 10px',
  marginLeft: '10px',
  textTransform: 'none',
  fontWeight: 'none',
  lineHeight: 'inherit'
}

styles.buttonUpgrade = {
  ...styles.buttonHeader,
  color: 'white',
  background: theme.palette.primary.main
}

styles.icon = {
  color: 'white',
  fontSize: '20px',
  justifyContent: 'right'
}

styles.tableHeader = css`
  font-size: 18px;
  text-align: left;
  border-bottom: 1px solid white;
  cursor: pointer;
`

styles.tableSortIcon = css`
  font-size: 9px;
  margin-left: 5px;
  position: relative;
  top: -2px;
`

styles.tableCell = css`
  font-size: 12px;
  padding: 12px 0;
  border-bottom: 1px solid white;
`

styles.nUsers = css`
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: end;
  padding-top: 16px;
  position: relative;
  padding-right: 16px;
`

function Users(props) {
  const [menuTableAnchorEl, setMenuTableAnchorEl] = React.useState(null)
  const [menuOrganizationUserAnchorEl, setMenuOrganizationUserAnchorEl] = React.useState(null)
  const [searchValue, setSearchValue] = React.useState('')
  const [hasPermission, setHasPermission] = React.useState(false)
  const [ownerOrganization, setOwnerOrganization] = React.useState(null)
  const [groupOrganizations, setGroupOrganizations] = React.useState([])
  const [filteredOrganizationUsers, setFilteredOrganizationUsers] = React.useState([])
  const [addUsersModal, setAddUsersModal] = React.useState(false)
  const [editOrganizationUser, setEditOrganizationUser] = React.useState(null)
  const [deactivateOrganizationUser, setDeactivateOrganizationUser] = React.useState(null)
  const [unlinkOrganizationUser, setUnlinkOrganizationUser] = React.useState(null)
  const [moreOptionsTable, setMoreOptionsTable] = React.useState(null)
  const [moreOptionsOrganizationUser, setMoreOptionsOrganizationUser] = React.useState(null)
  const [sortOrderAsc, setSortOrderAsc] = React.useState(true)
  const [sortByField, setSortByField] = React.useState('email')

  React.useEffect(() => {
    setOwnerOrganization(props.organizations[0])
    setGroupOrganizations(props.organizations.slice(1))

    setHasPermission(
      props.organizations[0].users.some(
        v => v.email === props.user.email && (v.role === 'owner' || v.role === 'admin')
      )
    )
  }, [props.organizations])

  React.useEffect(() => {
    if (!ownerOrganization) {
      return
    }

    if (searchValue) {
      const filtered = ownerOrganization.users.filter(organizationUser => {
        const fields = [
          organizationUser.email.toLowerCase(),
          ...organizationUser.organizations.map(v => v.organization.name.toLowerCase()),
          organizationUser.role.toLowerCase(),
          window.I18n.t(`users.roles.${organizationUser.role}`).toLowerCase(),
          organizationUser.user_key
            ? getPlanName(organizationUser.user_key).toLowerCase()
            : window.I18n.t('common.unassigned').toLowerCase()
        ]
        const user = organizationUser.user
        if (user) {
          fields.push(user.name.toLowerCase())
          fields.push(user.last_name.toLowerCase())
        }

        const search = searchValue.trim().toLowerCase()

        return fields.some(v => v.includes(search))
      })

      setFilteredOrganizationUsers(filtered)
    } else {
      setFilteredOrganizationUsers(ownerOrganization.users)
    }
  }, [searchValue, ownerOrganization])

  const sortBy = field => {
    if (field === sortByField) {
      setSortOrderAsc(!sortOrderAsc)
    } else {
      setSortByField(field)
      setSortOrderAsc(true)
    }
  }

  const onUpgrade = () => {
    history.push('/settings/plans')
  }

  const onAddUsers = () => {
    setAddUsersModal(true)
  }

  const onExportUsers = async users => {
    try {
      const header = [
        window.I18n.t('common.email'),
        window.I18n.t('users.firstName'),
        window.I18n.t('users.lastName'),
        window.I18n.t('users.role'),
        window.I18n.t('users.plan'),
        window.I18n.t('settings.groups')
      ]
      const rows = users.map(organizationUser => {
        const user = organizationUser.user
        const email = user
          ? user.email
          : `${organizationUser.email} (${window.I18n.t('common.pending')})`
        const name = user ? user.name : ''
        const lastName = user ? user.last_name : ''
        const role = window.I18n.t(`users.roles.${organizationUser.role}`)
        const groups = organizationUser.organizations.map(v => v.organization.name).join(', ')
        const planName = organizationUser.user_key
          ? getPlanName(organizationUser.user_key)
          : window.I18n.t('common.unassigned')

        return [email, name, lastName, role, planName, groups]
      })

      let csvContent = header.join(',')
      csvContent += '\r\n'
      rows.forEach(rowArray => {
        csvContent += rowArray.map(v => `"${v.replace(/"/g, '""')}"`).join(',')
        csvContent += '\r\n'
      })

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url

      const date = new Date()
      const dateString = `${date.getFullYear()}${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`
      a.download = `${dateString}-${ownerOrganization.name}-users.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting users:', error)
    }
  }

  const onEditOrganizationUser = user => {
    setEditOrganizationUser(user)
  }

  const onMoreOptionsTable = anchorEl => {
    setMenuTableAnchorEl(anchorEl)
    setMoreOptionsTable(true)
  }

  const onMoreOptionsTableClose = () => {
    setMoreOptionsTable(null)
    setMenuTableAnchorEl(null)
  }

  const onMoreOptionsOrganizationUser = (user, anchorEl) => {
    setMenuOrganizationUserAnchorEl(anchorEl)
    setMoreOptionsOrganizationUser(user)
  }

  const onMoreOptionsUserClose = () => {
    setMoreOptionsOrganizationUser(null)
    setMenuOrganizationUserAnchorEl(null)
  }

  const onDeactivateUser = user => {
    setDeactivateOrganizationUser(user)
  }

  const onUnlinkUser = user => {
    setUnlinkOrganizationUser(user)
  }

  const onUsersAdded = users => {
    setAddUsersModal(false)
  }

  const onUserEdited = user => {
    setEditOrganizationUser(null)
  }

  const onUserDeactivated = user => {
    setDeactivateOrganizationUser(null)
  }

  const onUserUnlinked = user => {
    setUnlinkOrganizationUser(null)
  }

  if (!ownerOrganization) {
    return (
      <div css={styles.loadingContainer}>
        <CircularProgress size={32} thickness={4} />
      </div>
    )
  }

  if (!hasPermission) {
    return (
      <div
        css={styles.descriptionContainer}
        dangerouslySetInnerHTML={{
          __html: window.I18n.t('groups.noPermissionDescription', {
            ownerEmail: ownerOrganization.owner_email,
            pre: '<b>',
            post: '</b>'
          })
        }}
      />
    )
  }

  const nLicenses = props.subscriptions.reduce((acc, v) => {
    let maxUsage = 0
    if (v.order) {
      for (const item of v.order.items) {
        // Skip order items without a base template (e.g. one-time purchases), they are not licenses.
        if (
          item.user_key &&
          item.user_key.licence_configuration &&
          item.user_key.licence_configuration.base_template
        ) {
          maxUsage += item.user_key.max_usage
        }
      }
    } else {
      maxUsage += v.userKey ? v.userKey.max_usage : 0
    }

    return acc + maxUsage
  }, 0)
  const availableLicenses = props.subscriptions.reduce((acc, v) => {
    let available = 0
    if (v.order) {
      for (const item of v.order.items) {
        // Skip order items without a base template (e.g. one-time purchases), they are not licenses.
        if (
          item.user_key &&
          item.user_key.licence_configuration &&
          item.user_key.licence_configuration.base_template
        ) {
          // Count pending users with the user-key assigned to them.
          let availableCount = item.user_key.max_usage - item.user_key.count_usage
          for (const organizationUser of ownerOrganization.users) {
            if (
              !organizationUser.user &&
              organizationUser.user_key &&
              organizationUser.user_key.code === item.user_key.code &&
              availableCount > 0
            ) {
              availableCount -= 1
            }
          }

          available += availableCount
        }
      }
    } else {
      let availableCount = 0

      // Count pending users with the user-key assigned to them.
      if (v.userKey) {
        availableCount = v.userKey.max_usage - v.userKey.count_usage
        for (const organizationUser of ownerOrganization.users) {
          if (
            !organizationUser.user &&
            organizationUser.user_key &&
            organizationUser.user_key.code === v.userKey.code &&
            availableCount > 0
          ) {
            availableCount -= 1
          }
        }
      }

      available += availableCount
    }

    return acc + available
  }, 0)
  const allowAddingUsers = nLicenses > 1 || ownerOrganization.users.length < 2

  const sortedAndFilteredUsers = [...filteredOrganizationUsers].sort((a, b) => {
    let aValue = ''
    let bValue = ''

    if (sortByField === 'email') {
      aValue = a.email.toLowerCase()
      bValue = b.email.toLowerCase()
    } else if (sortByField === 'firstName') {
      aValue = a.user ? a.user.name.toLowerCase() : ''
      bValue = b.user ? b.user.name.toLowerCase() : ''
    } else if (sortByField === 'lastName') {
      aValue = a.user ? a.user.last_name.toLowerCase() : ''
      bValue = b.user ? b.user.last_name.toLowerCase() : ''
    } else if (sortByField === 'role') {
      aValue = window.I18n.t(`users.roles.${a.role}`).toLowerCase()
      bValue = window.I18n.t(`users.roles.${b.role}`).toLowerCase()
    } else if (sortByField === 'plan') {
      aValue = a.user_key
        ? getPlanName(a.user_key).toLowerCase()
        : window.I18n.t('common.unassigned').toLowerCase()
      bValue = b.user_key
        ? getPlanName(b.user_key).toLowerCase()
        : window.I18n.t('common.unassigned').toLowerCase()
    } else if (sortByField === 'groups') {
      aValue = a.organizations
        .map(v => v.organization.name)
        .join(', ')
        .toLowerCase()
      bValue = b.organizations
        .map(v => v.organization.name)
        .join(', ')
        .toLowerCase()
    }

    if (aValue < bValue) {
      return sortOrderAsc ? -1 : 1
    }
    if (aValue > bValue) {
      return sortOrderAsc ? 1 : -1
    }
    return 0
  })

  let anchorTable = null

  return (
    <React.Fragment>
      {addUsersModal && (
        <AddOrganizationUsersModal
          user={props.user}
          organizationUsers={ownerOrganization.users}
          groupOrganizations={groupOrganizations}
          ownerOrganization={ownerOrganization}
          subscriptions={props.subscriptions}
          onApply={onUsersAdded}
        />
      )}

      {editOrganizationUser && (
        <EditOrganizationUserModal
          organizationUser={editOrganizationUser}
          groupOrganizations={groupOrganizations}
          ownerOrganization={ownerOrganization}
          subscriptions={props.subscriptions}
          onApply={onUserEdited}
        />
      )}

      {deactivateOrganizationUser && (
        <DeactivateOrganizationUserModal
          organizationUser={deactivateOrganizationUser}
          ownerOrganization={ownerOrganization}
          onApply={onUserDeactivated}
        />
      )}

      {unlinkOrganizationUser && (
        <UnlinkOrganizationUserModal
          organizationUser={unlinkOrganizationUser}
          ownerOrganization={ownerOrganization}
          onApply={onUserUnlinked}
        />
      )}

      {/* Options table. */}
      <Popper
        open={moreOptionsTable}
        anchorEl={menuTableAnchorEl}
        style={styles.menuHeader}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            id="menu-list-grow"
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper>
              <ClickAwayListener onClickAway={onMoreOptionsTableClose}>
                <MenuList>
                  <MenuItem
                    style={styles.menuItem}
                    onClick={() => {
                      onExportUsers(sortedAndFilteredUsers)
                    }}
                  >
                    {window.I18n.t('users.exportUsers')}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Options user. */}
      <Popper
        open={moreOptionsOrganizationUser}
        anchorEl={menuOrganizationUserAnchorEl}
        style={styles.menuHeader}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            id="menu-list-grow"
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper>
              <ClickAwayListener onClickAway={onMoreOptionsUserClose}>
                <MenuList>
                  <MenuItem
                    style={styles.menuItem}
                    onClick={() => {
                      onDeactivateUser(moreOptionsOrganizationUser)
                    }}
                  >
                    {window.I18n.t('common.deactivate')}
                  </MenuItem>
                  <MenuItem
                    style={{ ...styles.menuItem, color: 'red' }}
                    onClick={() => {
                      onUnlinkUser(moreOptionsOrganizationUser)
                    }}
                  >
                    {window.I18n.t('users.unlinkFromYourAccount')}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <div css={styles.container}>
        {availableLicenses === 0 && (
          <div css={styles.headerContainer}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={styles.buttonUpgrade}
              onClick={onUpgrade}
            >
              <Icon style={styles.headerIcon}>add</Icon>
              <span>{window.I18n.t('groups.moreLicensesDescription')}</span>
            </Button>
          </div>
        )}

        <div css={styles.subHeaderContainer}>
          <div css={styles.inputContainer}>
            <Icon style={styles.headerIcon}>search</Icon>
            <div
              css={styles.inputSearchContainer}
              style={{ borderBottom: searchValue ? '1px solid white' : '' }}
            >
              <input
                type="text"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                css={styles.inputSearch}
                placeholder={window.I18n.t('admin.search')}
              />
              {!!searchValue && (
                <Icon style={styles.closeIcon} onClick={() => setSearchValue('')}>
                  highlight_off
                </Icon>
              )}
            </div>
          </div>

          <div>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ ...styles.buttonUpgrade, ...(!allowAddingUsers ? { opacity: 0.5 } : {}) }}
              disabled={!allowAddingUsers}
              onClick={onAddUsers}
            >
              <Icon style={styles.headerIcon}>add</Icon>
              <span>{window.I18n.t('users.addUsers')}</span>
            </Button>
          </div>
        </div>

        <div style={{ width: '100%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th
                  css={styles.tableHeader}
                  style={{ width: '25%' }}
                  onClick={() => sortBy('email')}
                >
                  {window.I18n.t('common.email')}
                  <span css={styles.tableSortIcon}>
                    {sortByField === 'email' && (sortOrderAsc ? ' ▲' : ' ▼')}
                  </span>
                </th>
                <th
                  css={styles.tableHeader}
                  style={{ width: '15%' }}
                  onClick={() => sortBy('firstName')}
                >
                  {window.I18n.t('users.firstName')}
                  <span css={styles.tableSortIcon}>
                    {sortByField === 'firstName' && (sortOrderAsc ? ' ▲' : ' ▼')}
                  </span>
                </th>
                <th
                  css={styles.tableHeader}
                  style={{ width: '15%' }}
                  onClick={() => sortBy('lastName')}
                >
                  {window.I18n.t('users.lastName')}
                  <span css={styles.tableSortIcon}>
                    {sortByField === 'lastName' && (sortOrderAsc ? ' ▲' : ' ▼')}
                  </span>
                </th>
                <th
                  css={styles.tableHeader}
                  style={{ width: '10%' }}
                  onClick={() => sortBy('role')}
                >
                  {window.I18n.t('users.role')}
                  <span css={styles.tableSortIcon}>
                    {sortByField === 'role' && (sortOrderAsc ? ' ▲' : ' ▼')}
                  </span>
                </th>
                <th
                  css={styles.tableHeader}
                  style={{ width: '15%' }}
                  onClick={() => sortBy('plan')}
                >
                  {window.I18n.t('users.plan')}
                  <span css={styles.tableSortIcon}>
                    {sortByField === 'plan' && (sortOrderAsc ? ' ▲' : ' ▼')}
                  </span>
                </th>
                <th
                  css={styles.tableHeader}
                  style={{ width: '10%' }}
                  onClick={() => sortBy('groups')}
                >
                  {window.I18n.t('settings.groups')}
                  <span css={styles.tableSortIcon}>
                    {sortByField === 'groups' && (sortOrderAsc ? ' ▲' : ' ▼')}
                  </span>
                </th>
                <th
                  css={styles.tableHeader}
                  style={{
                    width: '10%',
                    textAlign: 'right',
                    paddingRight: '8px',
                    cursor: 'default'
                  }}
                >
                  <Button
                    aria-haspopup="true"
                    style={styles.icon}
                    onClick={() => onMoreOptionsTable(anchorTable)}
                    buttonRef={node => {
                      anchorTable = node
                    }}
                    aria-owns={moreOptionsOrganizationUser ? 'menu-list-grow' : undefined}
                  >
                    <Icon>settings</Icon>
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrganizationUsers.length === 0 && (
                <tr>
                  <td colSpan="7" css={styles.tableCell}>
                    {window.I18n.t('admin.NoResults')}
                  </td>
                </tr>
              )}
              {sortedAndFilteredUsers.map(organizationUser => {
                const isOwner = organizationUser.role === 'owner'
                const user = organizationUser.user
                const email = user
                  ? user.email
                  : `${organizationUser.email} (${window.I18n.t('common.pending')})`
                const name = user ? user.name : ''
                const lastName = user ? user.last_name : ''
                const role = window.I18n.t(`users.roles.${organizationUser.role}`)
                const groups = organizationUser.organizations
                  .map(v => v.organization.name)
                  .join(', ')
                const planName = organizationUser.user_key
                  ? getPlanName(organizationUser.user_key)
                  : window.I18n.t('common.unassigned')
                let anchorEl = null

                return (
                  <tr key={organizationUser.email}>
                    <td css={styles.tableCell}>{email}</td>
                    <td css={styles.tableCell}>{name}</td>
                    <td css={styles.tableCell}>{lastName}</td>
                    <td css={styles.tableCell}>{role}</td>
                    <td css={styles.tableCell}>{planName}</td>
                    <td css={styles.tableCell}>{groups}</td>
                    <td css={styles.tableCell} style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex' }}>
                        <Button
                          aria-haspopup="true"
                          style={styles.icon}
                          onClick={() => onEditOrganizationUser(organizationUser)}
                          buttonRef={node => {
                            anchorEl = node
                          }}
                          aria-owns={moreOptionsOrganizationUser ? 'menu-list-grow' : undefined}
                        >
                          <Icon>edit</Icon>
                        </Button>
                        {!isOwner && (
                          <Button
                            aria-haspopup="true"
                            style={{ ...styles.icon, paddingRight: '24px' }}
                            onClick={() =>
                              onMoreOptionsOrganizationUser(organizationUser, anchorEl)
                            }
                            buttonRef={node => {
                              anchorEl = node
                            }}
                            aria-owns={moreOptionsOrganizationUser ? 'menu-list-grow' : undefined}
                          >
                            <Icon>more_vert</Icon>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <span css={styles.nUsers}>
            {window.I18n.t('users.filteredUsers', {
              nUsers: filteredOrganizationUsers.length,
              totalUsers: ownerOrganization.users.length
            })}
          </span>
        </div>
      </div>
    </React.Fragment>
  )
}

Users.defaultProps = {}

Users.propTypes = {
  user: PropTypes.object.isRequired,
  organizations: PropTypes.array.isRequired,
  subscriptions: PropTypes.array.isRequired
}

export default Users
